import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { useLatestFrameStill } from '@/hooks/use-latest-frame-still';
import { useVideoFrameExtractor } from '@/hooks/use-video-frame-extractor';
import { frameExtractorUrl } from '@/lib/quoteCarousel/frameExtractorUrl';
import { EXPORT_FPS, formatFrameTime } from '@/lib/timeline/exportFrameTiming';

const PREVIEW_OPTIONS = {
  canvasWidth: 540,
  canvasHeight: 960,
  quality: 0.8,
  maxCachedFrames: 24,
  sampleFps: EXPORT_FPS,
  fitToSource: true,
};

export interface RenderedFrameScrubberProps {
  /** `finished_video_url`, untagged: the extractor URL gets the CORS cache key here. */
  videoUrl: string;
  /** Rendered video length in seconds; null while probing (slider disabled). */
  duration: number | null;
  /** width/height of the rendered video, for the still box. */
  exportAspect: number;
  /** Selected time on the rendered video, in seconds. */
  time: number;
  onTimeChange: (time: number) => void;
  /** Freezes the slider and the ±1 buttons (e.g. while the frame uploads). */
  disabled?: boolean;
  /** Tallest the still may be (CSS length). Default `45vh`. */
  maxStillHeight?: string;
}

/**
 * Frame-by-frame scrubber over the rendered clip, with a live still. The
 * still waits for the duration: until then the time is provisional.
 */
export function RenderedFrameScrubber({
  videoUrl,
  duration,
  exportAspect,
  time,
  onTimeChange,
  disabled = false,
  maxStillHeight = '45vh',
}: RenderedFrameScrubberProps) {
  const { t } = useTranslation();
  const { extractFrame } = useVideoFrameExtractor(frameExtractorUrl(videoUrl), PREVIEW_OPTIONS);
  const maxFrame = duration !== null && duration > 0 ? Math.max(0, Math.ceil(duration * EXPORT_FPS) - 1) : null;
  const requested = Math.max(0, Math.round(time * EXPORT_FPS));
  const frame = maxFrame === null ? requested : Math.min(requested, maxFrame);
  const frameTime = frame / EXPORT_FPS;
  const { still, loading } = useLatestFrameStill(extractFrame, frameTime, maxFrame !== null);

  const goToFrame = (target: number) => {
    if (disabled) return;
    const clamped = Math.max(0, maxFrame === null ? target : Math.min(maxFrame, target));
    onTimeChange(clamped / EXPORT_FPS);
  };

  return (
    <div className="space-y-3">
      <div
        className="relative mx-auto flex w-full items-center justify-center overflow-hidden rounded-md bg-black"
        style={{ aspectRatio: String(exportAspect), maxWidth: `calc(${maxStillHeight} * ${exportAspect})` }}
      >
        {still && (
          <img
            src={still}
            alt={t('clipCover.frame.previewAlt')}
            className="h-full w-full object-contain"
            data-testid="clip-cover-frame-preview"
          />
        )}
        {!loading && !still && maxFrame !== null && (
          <span className="px-3 text-center text-xs text-white/70" data-testid="clip-cover-frame-unavailable">
            {t('clipCover.frame.unavailable')}
          </span>
        )}
        {(loading || (!still && maxFrame === null)) && (
          <Loader2 className="absolute h-6 w-6 animate-spin text-white/70" aria-hidden="true" />
        )}
      </div>

      <div className="space-y-2">
        <Slider
          data-testid="clip-cover-frame-slider"
          min={0}
          max={maxFrame ?? Math.max(frame, 1)}
          step={1}
          value={[frame]}
          disabled={disabled || maxFrame === null}
          onValueChange={([value]) => goToFrame(value)}
          thumbProps={{ 'aria-label': t('clipCover.frame.scrubber') }}
        />
        <div className="flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="clip-cover-frame-prev"
            aria-label={t('clipCover.frame.prev')}
            disabled={disabled || maxFrame === null || frame <= 0}
            onClick={() => goToFrame(frame - 1)}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </Button>
          <span className="font-mono text-xs tabular-nums text-muted-foreground" data-testid="clip-cover-frame-time">
            {formatFrameTime(frameTime)}
            {duration !== null && duration > 0 ? ` / ${formatFrameTime(duration)}` : ''}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="clip-cover-frame-next"
            aria-label={t('clipCover.frame.next')}
            disabled={disabled || maxFrame === null || frame >= maxFrame}
            onClick={() => goToFrame(frame + 1)}
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
}
